import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Palette, Loader2 } from 'lucide-react';
import CoachingNugget from '../../components/CoachingNugget';

const BrandingSetupScreen = () => {
  const navigate = useNavigate();
  const { user: clerkUser } = useUser();
  
  const [formData, setFormData] = useState({
    agent: { firstName: '', lastName: '', email: '', phone: '', licenseNumber: '', licenseState: '' },
    brokerage: { name: '', licenseNumber: '', address: '' },
    brand: { primaryHex: '#16a34a', secondaryHex: '#0ea5e9', fontKey: 'default' }
  });
  const [saving, setSaving] = useState(false);

  // Pre-fill from Clerk user data
  useEffect(() => {
    if (clerkUser) {
      setFormData(prev => ({
        ...prev,
        agent: {
          ...prev.agent,
          firstName: prev.agent.firstName || clerkUser.firstName || '',
          lastName: prev.agent.lastName || clerkUser.lastName || '',
          email: prev.agent.email || clerkUser.primaryEmailAddress?.emailAddress || ''
        }
      }));
    }
  }, [clerkUser]);

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const isValid = formData.agent.firstName.trim().length > 0;

  const handleNext = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brand/profile`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agent: formData.agent,
          brokerage: formData.brokerage,
          brand: formData.brand
        })
      });
      console.log('[Onboarding] Brand profile saved successfully');
    } catch (err) {
      console.error('[Onboarding] Error saving brand profile:', err);
    } finally {
      setSaving(false);
      navigate('/onboarding/social-voice');
    }
  };

  const handleSkip = () => {
    navigate('/onboarding/social-voice');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4 pb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Palette className="w-6 h-6 text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">
              Set Up Your Brand
            </h1>
          </div>
          <p className="text-gray-600 text-sm">
            This info appears on your PDFs and reports. You can always update it later.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Section 1: Your Info */}
          <div className="space-y-3">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Your Info</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="firstName" className="text-xs text-gray-500 mb-1 block">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.agent.firstName}
                  onChange={(e) => handleInputChange('agent', 'firstName', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                  placeholder="John"
                />
              </div>
              <div>
                <Label htmlFor="lastName" className="text-xs text-gray-500 mb-1 block">Last Name</Label>
                <Input
                  id="lastName"
                  value={formData.agent.lastName}
                  onChange={(e) => handleInputChange('agent', 'lastName', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                  placeholder="Smith"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="email" className="text-xs text-gray-500 mb-1 block">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.agent.email}
                  onChange={(e) => handleInputChange('agent', 'email', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-xs text-gray-500 mb-1 block">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.agent.phone}
                  onChange={(e) => handleInputChange('agent', 'phone', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="licenseNumber" className="text-xs text-gray-500 mb-1 block">License #</Label>
                <Input
                  id="licenseNumber"
                  value={formData.agent.licenseNumber}
                  onChange={(e) => handleInputChange('agent', 'licenseNumber', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                  placeholder="12345678"
                />
              </div>
              <div>
                <Label htmlFor="licenseState" className="text-xs text-gray-500 mb-1 block">State</Label>
                <Input
                  id="licenseState"
                  value={formData.agent.licenseState}
                  onChange={(e) => handleInputChange('agent', 'licenseState', e.target.value.toUpperCase())}
                  maxLength={2}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                  placeholder="CA"
                />
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100"></div>

          {/* Section 2: Brokerage */}
          <div className="space-y-3">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Brokerage</label>
            <div>
              <Label htmlFor="brokerageName" className="text-xs text-gray-500 mb-1 block">Brokerage Name</Label>
              <Input
                id="brokerageName"
                value={formData.brokerage.name}
                onChange={(e) => handleInputChange('brokerage', 'name', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                placeholder="ABC Realty"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="brokerageLicense" className="text-xs text-gray-500 mb-1 block">Brokerage License # <span className="text-gray-400">(optional)</span></Label>
                <Input
                  id="brokerageLicense"
                  value={formData.brokerage.licenseNumber}
                  onChange={(e) => handleInputChange('brokerage', 'licenseNumber', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                  placeholder="BRK-12345"
                />
              </div>
              <div>
                <Label htmlFor="brokerageAddress" className="text-xs text-gray-500 mb-1 block">Address <span className="text-gray-400">(optional)</span></Label>
                <Input
                  id="brokerageAddress"
                  value={formData.brokerage.address}
                  onChange={(e) => handleInputChange('brokerage', 'address', e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/30 focus:border-primary text-sm"
                  placeholder="123 Main St"
                />
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100"></div>

          {/* Section 3: Brand Colors */}
          <div className="space-y-3">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Brand Colors</label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Primary Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.brand.primaryHex}
                    onChange={(e) => handleInputChange('brand', 'primaryHex', e.target.value)}
                    className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <Input
                    value={formData.brand.primaryHex}
                    onChange={(e) => handleInputChange('brand', 'primaryHex', e.target.value)}
                    className="flex-1 p-2 border border-gray-300 rounded-lg text-sm font-mono"
                    placeholder="#16a34a"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Secondary Color <span className="text-gray-400">(optional)</span></Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={formData.brand.secondaryHex}
                    onChange={(e) => handleInputChange('brand', 'secondaryHex', e.target.value)}
                    className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <Input
                    value={formData.brand.secondaryHex}
                    onChange={(e) => handleInputChange('brand', 'secondaryHex', e.target.value)}
                    className="flex-1 p-2 border border-gray-300 rounded-lg text-sm font-mono"
                    placeholder="#0ea5e9"
                  />
                </div>
              </div>
            </div>

            {/* Live Preview */}
            <div className="rounded-lg overflow-hidden border border-gray-200 mt-3">
              <div className="p-3 text-white text-xs" style={{ background: formData.brand.primaryHex }}>
                <div className="flex justify-between items-center">
                  <span className="font-bold">Property Analysis</span>
                  <span className="text-right opacity-90">Prepared by <strong>{formData.agent.firstName || 'You'}</strong></span>
                </div>
              </div>
              <div className="p-2 text-center text-xs text-gray-400">PDF header preview</div>
            </div>
          </div>

          <CoachingNugget>
            Setting up your brand early means every PDF and report you send looks professional from day one.
          </CoachingNugget>

          <Button
            onClick={handleNext}
            disabled={!isValid || saving}
            className="w-full h-12 text-lg bg-primary hover:bg-primary/90 disabled:opacity-50"
            data-testid="branding-continue-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Continue'
            )}
          </Button>

          <button 
            onClick={handleSkip} 
            className="w-full text-sm text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="branding-skip-btn"
          >
            Skip for now — I'll set this up later
          </button>
        </CardContent>
      </Card>
    </div>
  );
};

export default BrandingSetupScreen;
